<template>
    <button
        class="milo-fab"
        type="button"
        :class="face"
        :aria-label="chatStore.open ? $t('milo.close') : $t('milo.open')"
        :aria-expanded="chatStore.open"
        aria-controls="milo-sheet"
        @click="toggle"
    >
        <!-- The package's own poster: one file, the same crop the model is framed in,
             so nothing about the picture will jump the day the head itself arrives. -->
        <img
            v-if="face === 'poster'"
            class="milo-poster"
            :src="poster"
            alt=""
            @error="posterFailed = true"
        >

        <!-- No package at all — a mark in the house's accent colour that downloads
             nothing. It is the honest bottom of the ladder and not a placeholder: a
             portal served without the folder still gets a button that works. -->
        <span v-else class="milo-mark" aria-hidden="true">M</span>

        <span v-if="waiting" class="milo-dot"></span>
    </button>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue'

    import { miloHeavyAllowed, miloPosterCutoutUrl } from '@/libs/milo'
    import { useChatStore } from '@/stores/chat'

    /**
     * Milo's button: the door to the sheet, above the bottom bar and under a thumb.
     *
     * # Why a poster and not the head, in phase 1
     *
     * The head is 1.8 MB of renderer, model and poster, and this page opens in a car
     * park, on roaming, with the data saver on — a cost somebody else pays. The reception
     * portal asks only whether the machine has WebGL because a receptionist's machine is
     * on the hotel's own wire; `libs/milo.ts:miloHeavyAllowed()` is the guest's version of
     * that question and it asks about the CONNECTION as well.
     *
     * It is stubbed rather than wired, deliberately: the renderer is being replaced on
     * `parallel/milo-eyes`, which brings its own version of `public/milo/<version>/` and
     * its own asset base. Porting the model-viewer head here now would mean porting it
     * twice, so what ships is the poster from the same package — and the three faces
     * below are already the shape the head drops into.
     *
     * TODO(parallel/milo-eyes): set HEAD_AVAILABLE true, add `MiloHead.vue` beside this
     * file, and draw it where `face` says 'heavy'. Nothing else here has to change.
     */

    /** Whether this release carries a head to draw at all. See the note above. */
    const HEAD_AVAILABLE = false

    const chatStore = useChatStore()

    const posterFailed = ref(false)
    const poster       = miloPosterCutoutUrl()

    /**
     * Which of the three faces this device gets: the model, the package's poster, or a
     * plain mark. Asked once — a connection that changes mid-stay is not a reason to
     * swap the picture under somebody's thumb.
     */
    const face = computed((): string => {
        if (posterFailed.value) return 'mark'

        return HEAD_AVAILABLE && miloHeavyAllowed() ? 'heavy' : 'poster'
    })

    /** A dot for either of the two things a guest has not seen: an answer that landed
     *  while the sheet was shut, and a card nobody has decided. */
    const waiting = computed((): boolean => chatStore.unread || chatStore.proposalWaiting)

    const toggle = () => {
        if (chatStore.open) {
            chatStore.closeSheet()

            return
        }

        void chatStore.openSheet()
    }
</script>
